import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/NotificationsBell";
import MtaaSwitcher from "@/components/MtaaSwitcher";
import { JiraniLogo } from "@/components/JiraniLogo";
import { Menu, X, Users, Bell, Store, Wrench, CalendarDays, Home, LogOut, User as UserIcon, ShieldAlert, MessageCircle } from "lucide-react";

const nav = [
  { to: "/feed", label: "Feed", icon: Users, id: "nav-feed" },
  { to: "/alerts", label: "Alerts", icon: Bell, id: "nav-alerts" },
  { to: "/directory", label: "Directory", icon: Wrench, id: "nav-directory" },
  { to: "/marketplace", label: "Marketplace", icon: Store, id: "nav-marketplace" },
  { to: "/events", label: "Events", icon: CalendarDays, id: "nav-events" },
  { to: "/messages", label: "Inbox", icon: MessageCircle, id: "nav-messages", authOnly: true },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-black/5 bg-white/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <JiraniLogo />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {nav.filter((n) => !n.authOnly || user).map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={id}
              className={({ isActive }) =>
                `group flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#2E473B] text-white"
                    : "text-[#1A1A1A] hover:bg-[#2E473B]/10"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <MtaaSwitcher />
          {user && <NotificationsBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="jirani-pill flex items-center gap-2 rounded-full border border-black/5 bg-white px-2 py-1.5 pr-3 shadow-sm"
                >
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-[#2E473B] text-xs font-semibold text-white">
                    {initial}
                  </div>
                  <span className="hidden text-sm font-medium text-[#1A1A1A] sm:block">
                    {user.name?.split(" ")[0] || "You"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-profile"
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer"
                >
                  <UserIcon className="mr-2 h-4 w-4" /> Profile
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    data-testid="menu-admin"
                    onClick={() => navigate("/admin/moderation")}
                    className="cursor-pointer"
                  >
                    <ShieldAlert className="mr-2 h-4 w-4" /> Moderation queue
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="cursor-pointer text-[#C1292E]"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                className="rounded-full text-[#1A1A1A] hover:bg-[#2E473B]/10"
                onClick={() => navigate("/login")}
              >
                Log in
              </Button>
              <Button
                data-testid="nav-signup-btn"
                className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
                onClick={() => navigate("/register")}
              >
                Join Jirani
              </Button>
            </div>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="rounded-full border border-black/5 bg-white p-2 md:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-black/5 bg-white md:hidden">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6">
            <div className="grid grid-cols-2 gap-2">
              {nav.filter((n) => !n.authOnly || user).map(({ to, label, icon: Icon, id }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`${id}-mobile`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-full px-3 py-2 text-sm ${
                      isActive ? "bg-[#2E473B] text-white" : "bg-[#F9F7F4] text-[#1A1A1A]"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" /> {label}
                </NavLink>
              ))}
            </div>
            {!user && (
              <div className="mt-3 flex gap-2">
                <Button
                  data-testid="mobile-login-btn"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => { setOpen(false); navigate("/login"); }}
                >
                  Log in
                </Button>
                <Button
                  data-testid="mobile-signup-btn"
                  className="flex-1 rounded-full bg-[#D95D39] text-white hover:bg-[#c25333]"
                  onClick={() => { setOpen(false); navigate("/register"); }}
                >
                  Join Jirani
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
